import { useCallback } from "react";

import { ApiError } from "@steto-care/steto-query";

import { useAPIErrorMessageParser } from "@/hooks/useAPIErrorMessageParser.ts";
import { TelemonitoringTier } from "@/models/CarePlanModel.ts";
import type { ConditionType } from "@/models/ConditionModel.ts";
import { CreatePatientModel } from "@/models/CreatePatientModel.ts";
import { FileModel } from "@/models/FileModel.ts";
import type { GroupedInclusionPatient } from "@/models/GroupedInclusionModel.ts";
import type { InclusionAgentPatient } from "@/models/InclusionAgentModel.ts";
import type { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Patient, PatientID } from "@/models/PatientModel.ts";
import { TeleMonitoringCriteria } from "@/models/TelemonitoringCriteriaModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { CarePlanCreationInfoWithPrescription } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts";
import { CarePlanRenewalInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewal.ts";
import { NurseInclusionPatientCreationInfoWithCarePlan } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationInfo.ts";
import type { PatientInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfo.ts";
import { GeneratePrescriptionInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfo.ts";
import { termDateForCondition } from "@/uiKit/organisms/PatientCreationAndRenewal/termDateRules.ts";

export type ExtraData = {
  groupedInclusion?: GroupedInclusionPatient | undefined;
  inclusionAgent?: InclusionAgentPatient | undefined;
  nurseInclusion?: NurseInclusionPatient | undefined;
};

type GeneratePrescriptionRequest = {
  patientInfo: {
    givenName: string;
    familyName: string;
  };
  carePlan: {
    duration: string;
    description?: string;
    descriptionAttachment?: FileModel;
    inclusionCriteria: TeleMonitoringCriteria;
    inclusionTier: TelemonitoringTier;
    periodStart: string;
    diabetesType?: ConditionType;
    termDate?: string;
  };
  prescriptionInfo: {
    birthDate: string;
  };
};

export const useGeneratePrescription = () => {
  const generatePrescriptionMutation = Queries.prescription.useGeneratePrescription();

  return useCallback(
    (data: GeneratePrescriptionRequest, onSuccess: (data: { prescription: Blob }) => void) => {
      if (!data.carePlan.inclusionCriteria || !data.carePlan.duration) {
        return;
      }
      generatePrescriptionMutation.mutate(
        {
          first_name: data.patientInfo.givenName,
          last_name: data.patientInfo.familyName,
          birthDate: data.prescriptionInfo.birthDate ?? "",
          telemonitoringTier: data.carePlan.inclusionTier,
          duration: parseInt(data.carePlan.duration),
          periodStart: data.carePlan.periodStart,
          termDate: termDateForCondition(data.carePlan.diabetesType, data.carePlan.termDate),
        },
        {
          onSuccess: prescription => {
            onSuccess({ prescription });
          },
        },
      );
    },
    [generatePrescriptionMutation],
  );
};

const manageExtraData = (extraData?: ExtraData) => {
  if (!extraData) {
    return {};
  } else if (extraData.groupedInclusion) {
    return {
      grouped_inclusion_id: extraData.groupedInclusion?.id,
    };
  } else if (extraData.inclusionAgent) {
    return {
      inclusion_agent_id: extraData.inclusionAgent?.id,
    };
  } else if (extraData.nurseInclusion) {
    return {
      nurse_inclusion_id: extraData.nurseInclusion?.id,
    };
  }
  return {};
};

export const useCreateNoInsulinPatient = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
  extraData?: ExtraData,
) => {
  const createPatientMutation = Queries.practitioner.useCreatePatient();
  return useCallback(
    (
      patientInfo: PatientInfo,
      gestationInfo: {
        diabetesType: string;
        insulinScheme: string;
        termDate: string;
      },
    ) => {
      createPatientMutation.mutate(
        {
          patient: patientInfo,
          condition: {
            diabetesType: gestationInfo.diabetesType,
            insulinType: gestationInfo.insulinScheme,
            termDate: gestationInfo.termDate,
          },
          ...manageExtraData(extraData),
        },
        { onSuccess: onSuccess },
      );
    },
    [createPatientMutation, extraData],
  );
};

export const useCreatePatientAndCarePlan = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
  onError?: (error: ApiError) => void,
  extraData?: ExtraData,
) => {
  const createPatientMutation = Queries.practitioner.useCreatePatient();
  const errorMessage = useAPIErrorMessageParser();
  const callback = useCallback(
    (
      patientInfo: PatientInfo,
      carePlanInfo: CarePlanCreationInfoWithPrescription | NurseInclusionPatientCreationInfoWithCarePlan,
      prescriptionInfo: Partial<GeneratePrescriptionInfo>,
      prescription: FileModel,
    ) => {
      createPatientMutation.mutate(
        {
          patient: {
            ...patientInfo,
            birthDate: prescriptionInfo.birthDate ?? "",
          },
          care_plan: {
            duration: parseInt(carePlanInfo.duration),
            description: carePlanInfo.description ? carePlanInfo.description : undefined,
            descriptionAttachment: carePlanInfo.descriptionAttachment,
            treatment_prescription_attachment: carePlanInfo.treatmentPrescriptionAttachment,
            referral_letter_attachment: carePlanInfo.referralLetterAttachment,
            telemonitoringCriteria: carePlanInfo.inclusionCriteria,
            prescription: prescription,
            telemonitoring_nurse: carePlanInfo.telemonitoringNurseId,
          },
          condition: {
            diabetesType: carePlanInfo.diabetesType,
            insulinType: carePlanInfo.insulinScheme,
            termDate: termDateForCondition(carePlanInfo.diabetesType, carePlanInfo.termDate),
            pump_model: carePlanInfo.pump === "unknown" ? undefined : carePlanInfo.pump,
          },
          hsp_id: carePlanInfo.insulinScheme === "insulinPump" ? carePlanInfo.homeServiceProviderId : undefined,
          ...manageExtraData(extraData),
        },
        {
          onSuccess,
          onError: error => {
            const parsedError = errorMessage(error.data);
            if (parsedError) {
              TextToast.error(parsedError);
            }
            onError?.(error);
          },
        },
      );
    },
    [createPatientMutation, extraData],
  );

  return { callback, isPending: createPatientMutation.isPending };
};

export const useRenewPatient = (onSuccess: () => void) => {
  const renewPatientMutation = Queries.practitioner.useRenewCarePlan();
  const callback = useCallback(
    (
      patient: Patient,
      carePlanInfo: CarePlanRenewalInfo,
      prescriptionInfo: Partial<GeneratePrescriptionInfo>,
      prescription: FileModel,
    ) => {
      renewPatientMutation.mutate(
        {
          patientId: patient.id,
          data: {
            diabetesType: carePlanInfo.diabetesType,
            insulinType: carePlanInfo.insulinScheme,
            termDate: termDateForCondition(carePlanInfo.diabetesType, carePlanInfo.termDate),
            birthDate: prescriptionInfo?.birthDate ?? patient.birthDate,
            duration: parseInt(carePlanInfo.duration),
            description: carePlanInfo.description ? carePlanInfo.description : undefined,
            descriptionAttachment: carePlanInfo.descriptionAttachment,
            treatment_prescription_attachment: carePlanInfo.treatmentPrescriptionAttachment,
            referral_letter_attachment: carePlanInfo.referralLetterAttachment,
            telemonitoringCriteria: carePlanInfo.inclusionCriteria,
            prescription: prescription,
            pump_model: carePlanInfo.pump,
            telemonitoring_nurse: carePlanInfo.telemonitoringNurseId,
          },
        },
        { onSuccess },
      );
    },
    [renewPatientMutation],
  );

  return { callback, isPending: renewPatientMutation.isPending };
};

export const useSignPrescriptionAndCreatePatient = (
  onSuccess: (data: PatientID, variables: CreatePatientModel) => void,
  extraData?: ExtraData,
) => {
  const signPrescriptionMutation = Queries.prescription.useSignPrescription();
  const { callback: createPatientAndCarePlan, isPending: isCreatePending } = useCreatePatientAndCarePlan(
    onSuccess,
    undefined,
    extraData,
  );

  const submit = useCallback(
    (
      patientInfo: PatientInfo,
      carePlanInfo: CarePlanCreationInfoWithPrescription | NurseInclusionPatientCreationInfoWithCarePlan,
      prescriptionInfo: GeneratePrescriptionInfo,
    ) => {
      signPrescriptionMutation.mutate(
        {
          first_name: patientInfo.givenName,
          last_name: patientInfo.familyName,
          birthDate: prescriptionInfo.birthDate ?? "",
          telemonitoringTier: carePlanInfo.inclusionTier,
          duration: parseInt(carePlanInfo.duration),
          periodStart: carePlanInfo.periodStart,
        },
        {
          onSuccess: prescription =>
            createPatientAndCarePlan(patientInfo, carePlanInfo, prescriptionInfo, prescription),
        },
      );
    },
    [signPrescriptionMutation, createPatientAndCarePlan],
  );

  return {
    submit,
    isPending: signPrescriptionMutation.isPending || isCreatePending,
  };
};

export const useSignPrescriptionAndRenewPatient = (onSuccess: () => void) => {
  const signPrescriptionMutation = Queries.prescription.useSignPrescription();
  const { callback: renewPatient } = useRenewPatient(onSuccess);

  const submit = useCallback(
    (patient: Patient, carePlanInfo: CarePlanRenewalInfo, prescriptionInfo: GeneratePrescriptionInfo) => {
      signPrescriptionMutation.mutate(
        {
          first_name: patient.givenName,
          last_name: patient.familyName,
          birthDate: prescriptionInfo.birthDate ?? "",
          telemonitoringTier: carePlanInfo.inclusionTier,
          duration: parseInt(carePlanInfo.duration),
          periodStart: carePlanInfo.periodStart,
        },
        {
          onSuccess: prescription => renewPatient(patient, carePlanInfo, prescriptionInfo, prescription),
        },
      );
    },
    [signPrescriptionMutation, renewPatient],
  );

  return { submit, isPending: signPrescriptionMutation.isPending };
};
